/**
 * Lichte wrappers rond Tauri-plugins. Allemaal try/catch — de app draait ook in
 * een gewone browser tijdens dev (vite dev server), waar deze plugins niet
 * beschikbaar zijn.
 */

export async function isTauri(): Promise<boolean> {
  return isTauriSync();
}

/** Synchrone versie voor conditional rendering / setup-time checks
 *  waar je niet kunt awaiten. Detecteert dezelfde injected window-prop
 *  die Tauri's WebView zet voordat onze JS draait. */
export function isTauriSync(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export async function setupAutostart(enable: boolean): Promise<void> {
  try {
    const { enable: en, disable, isEnabled } = await import("@tauri-apps/plugin-autostart");
    const already = await isEnabled();
    if (enable && !already) await en();
    if (!enable && already) await disable();
  } catch {
    /* geen Tauri-context */
  }
}

async function runUpdateCheck(): Promise<void> {
  try {
    const { check } = await import("@tauri-apps/plugin-updater");
    const update = await check();
    if (!update) return;
    await update.downloadAndInstall();
    const { relaunch } = await import("@tauri-apps/plugin-process");
    await relaunch();
  } catch (err) {
    console.error("[updater] FAILED:", err);
  }
}

/**
 * Plant een silent update-check op een moment dat de gebruiker waarschijnlijk
 * niet recht in het scherm kijkt: ofwel als het venster z'n focus verliest
 * (gebruiker pakt iets anders), ofwel na een vaste vertraging — afhankelijk
 * van wat eerst gebeurt. Zo merkt niemand de herstart.
 */
export function checkForUpdate(): void {
  let triggered = false;
  const fire = () => {
    if (triggered) return;
    triggered = true;
    void runUpdateCheck();
  };

  // Triggert als de gebruiker even wegklikt of een andere window naar voren
  // haalt — het natuurlijke moment om stil iets te vervangen.
  const onBlur = () => fire();
  window.addEventListener("blur", onBlur, { once: true });

  // Fallback: na 60s alsnog, zodat een gebruiker die de hele tijd in Swift
  // blijft hangen óók upgrades binnenkrijgt (al ervaart die wel de restart).
  setTimeout(() => {
    window.removeEventListener("blur", onBlur);
    fire();
  }, 60_000);
}

// "1.12.0" → "1.12" voor weergave: de patch-nul is ruis in de titelbalk,
// en zo matcht het getal met de Android-versionName (die "1.12" is).
const DISPLAY_VERSION = __APP_VERSION__.replace(/\.0$/, "");

export async function setBadgeUnread(count: number): Promise<void> {
  try {
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    const win = getCurrentWindow();
    const base = `Swift ${DISPLAY_VERSION}`;
    await win.setTitle(count > 0 ? `${base} (${count})` : base);
  } catch {
    /* */
  }
}

export async function focusWindow(): Promise<void> {
  try {
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    const win = getCurrentWindow();
    await win.show();
    await win.unminimize();
    await win.setFocus();
  } catch {
    /* */
  }
}

/**
 * Vraagt de gebruiker's aandacht: op Windows knippert de taskbar-icoon
 * tot 'ie de app focust. Tauri's setUserAttention met "Informational"
 * mapped daar 1:1 op. Geen no-op op andere platforms — best-effort.
 */
export async function flashTaskbar(): Promise<void> {
  try {
    const { getCurrentWindow, UserAttentionType } = await import("@tauri-apps/api/window");
    const win = getCurrentWindow();
    await win.requestUserAttention(UserAttentionType.Informational);
  } catch {
    /* */
  }
}

/**
 * De hostnaam van deze machine (Windows-computernaam), als sessie-/apparaat-
 * label. In dev (browser zonder Tauri) of als de plugin faalt → null, dan
 * kiest de caller een fallback.
 */
export async function getHostname(): Promise<string | null> {
  try {
    const { hostname } = await import("@tauri-apps/plugin-os");
    const h = await hostname();
    return h && h.trim() ? h.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Hoe lang heeft de gebruiker het systeem niet aangeraakt (muis, toetsen),
 * in seconden. Onder Windows leest dit GetLastInputInfo — dus machine-breed,
 * ook als Swift niet actief is. In dev (browser zonder Tauri) of op andere
 * platforms returnt 'ie null en moet de caller fallback-gedrag kiezen.
 */
export async function getMachineIdleSeconds(): Promise<number | null> {
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const v = await invoke<number>("get_idle_seconds");
    return typeof v === "number" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Schrijft een blob naar het system-temp dir met een uniek prefix en
 * opent het bestand met de default-app (bv. Word, beeldviewer, PDF).
 */
export async function saveAndOpenBlob(blob: Blob, filename: string): Promise<void> {
  const [{ writeFile, BaseDirectory }, { tempDir, join }, { openPath }] = await Promise.all([
    import("@tauri-apps/plugin-fs"),
    import("@tauri-apps/api/path"),
    import("@tauri-apps/plugin-opener"),
  ]);
  const safe = filename.replace(/[^A-Za-z0-9._-]/g, "_");
  const unique = `swift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safe}`;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  await writeFile(unique, bytes, { baseDir: BaseDirectory.Temp });
  const dir = await tempDir();
  const full = await join(dir, unique);
  await openPath(full);
}
